import React, { useCallback, useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  // ChatCenteredText,
  Download,
  DownloadSimple,
  Eye,
  Notification,
  Share,
} from "phosphor-react";
import { getFarm } from "api/Farm";
import { getStackOrders, getZoningOrders } from "api/Orders";
import Loading from "components/Loading";
import useOnClickOutside from "hooks/useOnClickOutside";
import Button from "components/Button";
import "./index.scss";
import Divider from "components/elements/Divider";

const ProcessingAnimation = () => {
  return (
    <div class="progress">
      <div class="color" />
      <p className="text">Processing...</p>
    </div>
  );
};

const PaymentSuccessPage = () => {
  const history = useHistory();
  const location = useLocation();
  if (location.state === undefined) {
    history.goBack();
  }
  const { token, paymentId } = location?.state;
  const exportOptionRef = useRef(null);
  const [openExportMenu, setOpenExportMenu] = useState(null);
  const [stackOrders, setStackOrders] = useState(null);
  const [zoneOrders, setZoneOrders] = useState(null);
  const [stackOrderStatus, setStackOrderStatus] = useState("");
  const [isZoneLoading, setIsZoneLoading] = useState(false);
  const [isStackLoading, setIsStackLoading] = useState(false);
  useOnClickOutside(exportOptionRef, () => setOpenExportMenu(null));

  const getZoneFarmDetails = useCallback(
    async (items) => {
      const farmData = [];
      await Promise.all(
        items.map(async (item) => {
          try {
            const farmDetails = await getFarm(item.farm_id, token);
            farmData.push(farmDetails);
          } catch (e) {
            toast.error("Fetching Farm details error.");
          }
        })
      );
      const successOrders = [];
      items.forEach((item) => {
        farmData.forEach((i) => {
          if (i.properties.farm_id === item.farm_id) {
            successOrders.push({
              ...item,
              farm: i,
            });
          }
        });
      });
      const result = [
        ...successOrders
          .reduce((a, c) => {
            a.set(c.id, c);
            return a;
          }, new Map())
          .values(),
      ];
      setZoneOrders(result);
      setIsZoneLoading(false);
    },
    [token]
  );

  const getStackFarmDetails = useCallback(
    async (items) => {
      const farmData = [];
      await Promise.all(
        items.map(async (item) => {
          try {
            const farmDetails = await getFarm(item.farm_id, token);
            farmData.push(farmDetails);
          } catch (e) {
            toast.error("Fetching Farm details error.");
          }
        })
      );
      const successOrders = [];
      items.forEach((item) => {
        farmData.forEach((i) => {
          if (i.properties.farm_id === item.farm_id) {
            successOrders.push({
              ...item,
              farm: i,
            });
          }
        });
      });
      const result = [
        ...successOrders
          .reduce((a, c) => {
            a.set(c.id, c);
            return a;
          }, new Map())
          .values(),
      ];
      setStackOrders(result);
      setIsStackLoading(false);
    },
    [token]
  );

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setIsZoneLoading(true);
      try {
        const query = `&payment_id=${paymentId}&status=paid`;
        const orderDetails = await getZoningOrders(query, token);
        if (orderDetails) {
          setZoneOrders(orderDetails.data);
          getZoneFarmDetails(orderDetails.data);
        }
      } catch (e) {
        toast.error(e);
        setIsZoneLoading(false);
      }
    };

    if (paymentId) {
      fetchOrderDetails();
    }
  }, [paymentId, token, getZoneFarmDetails]);

  useEffect(() => {
    const fetchStackOrderDetails = async () => {
      setIsStackLoading(true);
      try {
        const stackQuery = `&payment_id=${paymentId}`;
        const stackOrderDetails = await getStackOrders(stackQuery, token);
        if (stackOrderDetails.data) {
          setStackOrders(stackOrderDetails.data);
          getStackFarmDetails(stackOrderDetails.data);
          setStackOrderStatus(stackOrderDetails.data[0].status);
        }
      } catch (e) {
        toast.error(e);
        setIsStackLoading(false);
      }
    };
    fetchStackOrderDetails();

    const pollingInterval = setInterval(() => {
      fetchStackOrderDetails();
      if (stackOrderStatus === "completed") {
        clearInterval(pollingInterval);
      }
    }, 5000);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [token, getStackFarmDetails, paymentId, stackOrderStatus]);

  const toggleExportMenu = (event, id) => {
    setOpenExportMenu(id);
    event.stopPropagation();
  };

  const renderZoneExportOptions = () => {
    const element = document.getElementById(`export-${openExportMenu}`);
    const { bottom } = element.getBoundingClientRect();
    const order = zoneOrders?.find((item) => item.id === openExportMenu);
    return (
      <div
        className="export-options"
        style={{ top: bottom }}
        ref={exportOptionRef}
      >
        <>
          <div className="label">Export as</div>
          <div className="items">
            <a
              href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/zoning/download/${order.download_key}?unified=false`}
              className="item"
            >
              <Download size={20} color="#6F7D68" className="icon" />
              Individual Fields ({order.fields.length}{" "}
              {order.fields.length > 1 ? "Files" : "File"})
            </a>
            <a
              href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/zoning/download/${order.download_key}?unified=true`}
              className="item"
            >
              <Download size={20} color="#6F7D68" className="icon" />
              Entire Farm (1 File)
            </a>
          </div>
        </>
      </div>
    );
  };

  const renderStackExportOptions = () => {
    const element = document.getElementById(`export-${openExportMenu}`);
    const { bottom } = element.getBoundingClientRect();
    const order = stackOrders?.find((item) => item.id === openExportMenu);
    return (
      <div
        className="export-options"
        style={{ top: bottom }}
        ref={exportOptionRef}
      >
        <>
          <div className="label">Export as</div>
          <div className="items">
            <a
              href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/stack/download/${order.download_key}?unified=false`}
              className="item"
            >
              <Download size={20} color="#6F7D68" className="icon" />
              Individual Fields ({order.field_ids.length}{" "}
              {order.field_ids.length > 1 ? "Files" : "File"})
            </a>
            <a
              href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/stack/download/${order.download_key}?unified=true`}
              className="item"
            >
              <Download size={20} color="#6F7D68" className="icon" />
              Entire Farm (1 File)
            </a>
          </div>
        </>
      </div>
    );
  };

  const handleViewOrder = (order) => {
    history.push({
      pathname: `/farms/${order?.farm_id}`,
      search: `?zone=${order.id}`,
    });
  };

  const handleViewStackOrder = (order) => {
    history.push({
      pathname: `/farms/${order?.farm_id}`,
      search: `?stack=${order.id}`,
    });
  };

  return (
    <div className="success-payment-wrapper">
      <div className="container">
        <div className="headline">
          <p className="exclamation">What&lsquo;s next for you</p>
        </div>
        {isZoneLoading ? (
          <div className="load">
            <Loading />
          </div>
        ) : (
          <>
            {zoneOrders && zoneOrders.length ? (
              <div className="container-wrapper">
                <div className="details">
                  <p className="tile-header">Zone Map files</p>
                  {zoneOrders &&
                    zoneOrders.map((item) => (
                      <div className="details-wrapper">
                        <div className="farm-details" key={item}>
                          <p className="label">
                            {item?.farm?.properties?.farm_name}
                          </p>
                          <div className="sub-details">
                            <p>{item?.num_zones}5 zones</p>
                            <p className="eclipse" />
                            <p>{item?.fields?.length} fields</p>
                          </div>
                        </div>
                        <div className="options">
                          <>
                            <div
                              id={`export-${item.id}`}
                              className="share-action"
                              aria-hidden
                            >
                              <Share size={24} color="#6F7D68" />
                            </div>
                            <div
                              id={`export-${item.id}`}
                              className="export-action"
                              aria-hidden
                              onClick={(event) =>
                                toggleExportMenu(event, item.id)
                              }
                            >
                              <DownloadSimple size={24} color="#6F7D68" />
                            </div>
                            <div
                              className="view-zones"
                              aria-hidden
                              onClick={() => handleViewOrder(item)}
                            >
                              <Eye size={24} color="#6F7D68" />
                            </div>
                          </>
                        </div>
                      </div>
                    ))}
                </div>
                {openExportMenu && renderZoneExportOptions()}
              </div>
            ) : null}
          </>
        )}
        <>
          {stackOrders && stackOrders.length ? (
            <div className="container-wrapper">
              <div className="details">
                <p className="tile-header">Stack Analysis files</p>
                {stackOrders &&
                  stackOrders.map((item) => (
                    <div className="details-wrapper">
                      <div className="farm-details" key={item}>
                        <p className="label">
                          {item?.farm?.properties?.farm_name}
                        </p>
                        <div className="sub-details">
                          <p>{item.dates?.length} Years</p>
                          <p className="eclipse" />
                          <p>{item?.field_ids?.length} fields</p>
                        </div>
                      </div>
                      <div className="options -processing">
                        {!isStackLoading && item?.status === "completed" ? (
                          <>
                            {/* <div
                              id={`export-${item.id}`}
                              className="share-action"
                              aria-hidden
                            >
                              <Share size={24} color="#6F7D68" />
                            </div> */}
                            <div
                              id={`export-${item.id}`}
                              className="export-action"
                              aria-hidden
                              onClick={(event) =>
                                toggleExportMenu(event, item.id)
                              }
                            >
                              <DownloadSimple size={24} color="#6F7D68" />
                            </div>
                            <div
                              className="view-zones"
                              aria-hidden
                              onClick={() => handleViewStackOrder(item)}
                            >
                              <Eye size={24} color="#6F7D68" />
                            </div>
                          </>
                        ) : (
                          <ProcessingAnimation />
                        )}
                      </div>
                    </div>
                  ))}
                <Divider />
                <div className="tile-footer">
                  <Notification size={24} color="#38B000" />
                  <p className="footer-title">Track your Stack</p>
                  <p className="footer-content">
                    To keep you on the loop, Any changes regarding your analysis
                    will be emailed to you.
                  </p>
                </div>
              </div>
              {openExportMenu && renderStackExportOptions()}
            </div>
          ) : null}
        </>
        {/* For future use */}
        {/* <div className="container-wrapper">
          <div className="details">
            <p className="tile-header">Imagery Orders</p>
            <div className="tile-footer">
              <Notification size={24} color="#38B000" />
              <p className="footer-title">Order updates</p>
              <p className="footer-content">
                We will keep you on track as we deliver your imagery. A
                notification will be sent to you if any problems occur
                concerning your order.
              </p>
            </div>
            <div className="tile-footer">
              <ChatCenteredText size={24} color="#38B000" />
              <p className="footer-title">Questions</p>
              <p className="footer-content">
                Any questions or concerns, please contact us immediately at
                support@datafarming.com.au
              </p>
            </div>
          </div>
        </div> */}
        <div className="button-action">
          <Button
            label="Back to dashboard page"
            type="solid"
            color="black"
            onClick={() => history.push("/dashboard")}
            rounded
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
